import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   EventEmitter,
   Input,
   Output,
   ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { DeviceTypes } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { saveMostRecentDeviceType } from 'src/app/store/user-preferences/user-preferences.actions';
import { getMostRecentDeviceTypeUserPreference } from 'src/app/store/user-preferences/user-preferences.selectors';
import { AddDeviceData } from '../models/add-device-data';
import * as addDevice from '../state/add-device.actions';
import { getDeviceTypeFromStore, getMostRecentAccount } from '../state/add-device.selectors';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-add-device-view',
    styleUrls: ['./add-device-view.component.scss'],
    templateUrl: './add-device-view.component.html',
    standalone: false
})
export class AddDeviceViewComponent {
   @ViewChild('account') account: ElementRef;

   @Input() callState: CallState;

   @Output() formSubmitted = new EventEmitter<AddDeviceData>();

   @Output() saveAndAddAnother = new EventEmitter<AddDeviceData>();

   @Output() formCanceled = new EventEmitter<void>();

   @Input() form: UntypedFormGroup;

   deviceTypes = Object.values(DeviceTypes);

   mostRecentDeviceType$: Observable<string | null>;

   mostRecentAccount$: Observable<{ accountId: number | null; name: string | null } | null>;

   private destroyed$ = new Subject<void>();

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private changeDetectorRef: ChangeDetectorRef
   ) {
      this.mostRecentDeviceType$ = this.store.pipe(select(getMostRecentDeviceTypeUserPreference));
      this.mostRecentAccount$ = this.store.pipe(select(getMostRecentAccount));
   }

   ngOnInit() {
      this.actions$.pipe(ofType(addDevice.createDeviceAndAddAnotherSuccess)).subscribe(() => {
         this.clearDeviceFields();
         this.form.markAsPristine();
         this.form.markAsUntouched();
         this.account.nativeElement.focus();
      });
      this.mostRecentDeviceType$.subscribe((mostRecentDeviceType) => {
         if (mostRecentDeviceType) {
            const deviceType = this.deviceTypes.find((dt) => dt === mostRecentDeviceType);
            this.form.get('deviceTypeId')?.setValue(deviceType);
         }
      });
      this.mostRecentAccount$.subscribe((mostRecentAccount) => {
         if (mostRecentAccount) {
            this.form
               .get('account')
               ?.setValue({ accountId: mostRecentAccount.accountId, name: mostRecentAccount.name });
         } else {
            this.form.get('account')?.setValue(null);
         }
      });
      this.actions$.pipe(ofType(addDevice.setDeviceTypeOnForm)).subscribe((action) => {
         this.form.get('deviceTypeId')?.setValue(action.deviceType);
      });
      this.store.select(getDeviceTypeFromStore).subscribe((deviceType) => {
         if (deviceType) {
            this.form.get('deviceTypeId')?.setValue(deviceType);
         }
      });
   }

   clearDeviceFields() {
      this.form.get('deviceTypeId')?.setValue('');
      this.form.get('macAddress')?.setValue('');
      this.form.get('account')?.setValue(null);
      this.changeDetectorRef.markForCheck();
   }

   onCloseClick(): void {
      this.formCanceled.emit();
   }

   onCreate(): void {
      if (this.form.valid) {
         this.formSubmitted.emit();
         this.store.dispatch(saveMostRecentDeviceType({ mostRecentDeviceType: this.form.get('deviceTypeId')?.value }));
      }
   }

   onCreateAndAddAnother() {
      if (this.form.valid) {
         this.saveAndAddAnother.emit();
         this.store.dispatch(saveMostRecentDeviceType({ mostRecentDeviceType: this.form.get('deviceTypeId')?.value }));
      } else {
         this.form.markAllAsTouched();
      }
   }
   deviceDisplayWith(option: DeviceTypes): string {
      return `${option ?? ''}`;
   }

   deviceSearch(searchText: string) {
      this.deviceTypes = Object.values(DeviceTypes).filter((device) =>
         device.toUpperCase().includes(searchText.toUpperCase())
      );
   }

   deviceSearchReset() {
      this.deviceTypes = Object.values(DeviceTypes);
   }
}
